import { extractKeys } from '@craft/lib/primitives';

import { addQueryParamsToUrl } from '@/src/lib/urls';

/**
 * Defines an identifier for the authorization parameters that are forwarded to the Auth0 SDK,
 * either in the query string of the login URL or attached to the authorization params (login
 * options).
 *
 * The values of these parameters are intentionally not represented as the actual query parameter
 * names or authorization parameter keys used by the Auth0 SDK. These are merely references to the
 * various parameters and not actual values that can be used directly.
 */
export enum Auth0QueryParameter {
  Connection = '__connection__',
  Organization = '__org__',
  ReturnTo = '__return_to__',
}

const LogoutPath = '/auth/logout' satisfies `/${string}`;

const LoginPath = '/auth/login' satisfies `/${string}`;

/**
 * Defines the query parameter keys that are used to include authorization information in the URL
 * for the login route.
 *
 * These query parameters are forwarded automatically by Auth0, so the parameters in the URL must
 * have the same keys as the authorization params (login options) expected by the Auth0 SDK.
 *
 * @example
 * ```ts
 * /auth/login?connection=google-oauth2&organization=org_123&returnTo=/dashboard
 * ```
 */
const Auth0QueryParameterKeys = {
  [Auth0QueryParameter.Connection]: 'connection',
  [Auth0QueryParameter.Organization]: 'organization',
  [Auth0QueryParameter.ReturnTo]: 'returnTo',
} as const satisfies Record<Auth0QueryParameter, string>;

/**
 * Defines the actual query parameter key that should be used for a given parameter name.
 *
 * @example
 * ```ts
 * // 'connection'
 * Auth0QueryParameterKey<Auth0QueryParameter.Connection>
 * ```
 */
type Auth0QueryParameterKey<K extends Auth0QueryParameter = Auth0QueryParameter> =
  (typeof Auth0QueryParameterKeys)[K];

/**
 * A class that is used to encapsulate configuration constants for Auth0.
 */
export class Auth0Config {
  /**
   * Instantiates an instance of {@link Auth0Config}.
   */
  public static fromEnvironment() {
    return new Auth0Config();
  }

  public getQueryParameterKey<K extends Auth0QueryParameter>(param: K): Auth0QueryParameterKey<K> {
    return Auth0QueryParameterKeys[param];
  }

  public loginPath<K extends Auth0QueryParameter>(queryParams: Record<K, string>) {
    return addQueryParamsToUrl(LoginPath, this.mapQueryParams(queryParams));
  }

  /**
   * Maps the provided object of query parameter values, indexed by the parameter identifiers
   * defined by {@link Auth0QueryParameter}, to an object indexed by the actual query parameter
   * keys expected by the Auth0 SDK.
   */
  public mapQueryParams<K extends Auth0QueryParameter>(
    params: Record<K, string>,
  ): Record<Auth0QueryParameterKey<K>, string> {
    const result: Record<Auth0QueryParameterKey<K>, string> = {} as Record<
      Auth0QueryParameterKey<K>,
      string
    >;
    for (const param of extractKeys(params)) {
      const key = this.getQueryParameterKey(param);
      result[key] = params[param];
    }
    return result;
  }

  public get logoutPath() {
    return LogoutPath;
  }
}
